import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormField,
  Input,
  Modal,
  Select,
  SelectItem,
} from '@ntconsult/aluma-react';
import { useCreateUser } from '../hooks/useCreateUser';
import { useUpdateUser } from '../hooks/useUpdateUser';
import type { User, UserRole } from '../types';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
}

export function UserFormModal({ isOpen, onClose, user }: UserFormModalProps) {
  const { t } = useTranslation(['users', 'common']);
  const titleId = useId();
  const formId = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('user');

  const { mutate: createUser, isPending: isCreating } = useCreateUser();
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();

  const isEditing = !!user;
  const isPending = isCreating || isUpdating;

  const roleOptions = [
    { value: 'user', label: t('users:roles.user') },
    { value: 'manager', label: t('users:roles.manager') },
    { value: 'admin', label: t('users:roles.admin') },
  ];

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setPassword('');
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setRole('user');
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      updateUser(
        { id: user.id, data: { role } },
        { onSuccess: onClose }
      );
    } else {
      createUser(
        { name, email, password, role },
        { onSuccess: onClose }
      );
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      ariaLabelledBy={titleId}
      size="sm"
    >
      <Modal.Header
        titleId={titleId}
        title={isEditing ? t('users:editUser') : t('users:newUser')}
        onClose={onClose}
      />
      <Modal.Body>
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label={t('users:form.name')} required={!isEditing}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('users:form.namePlaceholder')}
              disabled={isEditing}
              autoFocus={!isEditing}
            />
          </FormField>

          <FormField label={t('users:form.email')} required={!isEditing}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('users:form.emailPlaceholder')}
              disabled={isEditing}
            />
          </FormField>

          {!isEditing && (
            <FormField label={t('users:form.password')} required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('users:form.passwordPlaceholder')}
              />
            </FormField>
          )}

          <FormField label={t('users:form.role')}>
            <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </FormField>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" form={formId} variant="primary" loading={isPending}>
          {isEditing ? t('common:actions.save') : t('users:createUser')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
